import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck, Loader2, Sparkles } from "lucide-react";
import { StarField } from "@/components/StarField";
import { Logo } from "@/components/Logo";
import { Disclaimer } from "@/components/Disclaimer";
import { LuckyDrawBadge } from "@/components/PlansGrid";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `₹${Number(n).toFixed(2)}`;

export default function Subscribe() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [cfg, setCfg] = useState({ monthly_price: 499, gst_percent: 18 });
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(params.get("plan") || "monthly");
  const [sub, setSub] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const plan = plans.find((p) => p.id === selected) || plans[0];
  const gstPct = cfg.gst_percent ?? 18;
  const base = plan?.price ?? cfg.monthly_price;
  const gst = +(base * gstPct / 100).toFixed(2);
  const total = +(base + gst).toFixed(2);

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const activate = async (verifyBody) => {
    await api.post("/subscription/verify", verifyBody);
    await refresh();
    toast.success("Subscription activated! Welcome aboard ✨");
    nav("/dashboard");
  };

  const pay = async () => {
    setProcessing(true);
    let opened = false;
    try {
      const { data: order } = await api.post("/subscription/create-order", { plan_id: selected });
      if (order.provider === "razorpay") {
        const ok = await loadRzp();
        if (!ok) throw new Error("Could not load payment gateway");
        const rzp = new window.Razorpay({
          key: order.key_id,
          subscription_id: order.subscription_id,
          name: "MyssticChat",
          description: `${order.plan?.name || "Monthly"} · UPI Autopay / e-mandate`,
          theme: { color: "#E5C158" },
          handler: async (resp) => {
            try {
              await activate({ razorpay_payment_id: resp.razorpay_payment_id, razorpay_subscription_id: resp.razorpay_subscription_id, razorpay_signature: resp.razorpay_signature });
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed"); }
            finally { setProcessing(false); }
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setProcessing(false); });
        rzp.open();
        opened = true;
        return;
      }
      await new Promise((r) => setTimeout(r, 1200));
      await activate({ razorpay_order_id: order.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: "mock_signature" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment failed. Please try again.");
    } finally {
      if (!opened) setProcessing(false);
    }
  };

  const active = sub?.status === "active";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#080B1A] via-[#0E1229] to-[#12102B] relative flex items-center justify-center px-4 py-12">
      <StarField count={70} />
      <div className="relative z-10 w-full max-w-2xl">
        <div className="flex justify-center mb-8"><Logo textClass="text-2xl" iconSize={26} /></div>

        {/* plan selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="plan-selector">
          {plans.map((p) => (
            <button key={p.id} data-testid={`plan-pill-${p.id}`} onClick={() => setSelected(p.id)}
              className={`rounded-full px-4 py-2 text-sm transition ${selected === p.id ? "btn-gold" : "border border-amber-500/25 text-slate-300 hover:text-amber-200"}`}>
              {p.name} · ₹{p.price}
            </button>
          ))}
        </div>

        <div className="relative p-[1px] rounded-3xl bg-gradient-to-br from-amber-500/50 via-amber-200/20 to-purple-500/30">
          <div className="rounded-3xl bg-[#0E132A] p-8" data-testid="subscribe-card">
            <div className="text-center">
              <span className="text-xs uppercase tracking-[0.2em] text-amber-400/90">MyssticChat {plan?.name}</span>
              <div className="mt-4 flex items-end justify-center gap-2">
                <span className="font-serif text-6xl text-slate-50">₹{base}</span>
                <span className="text-slate-400 mb-2">/ {plan?.validity_months === 1 ? "month" : `${plan?.validity_months} months`}</span>
              </div>
              <p className="mt-1 text-sm text-amber-200/80">Questions at ₹{plan?.per_question_price} each + GST</p>
            </div>
            <ul className="mt-7 space-y-3">
              {(plan?.benefits || []).filter((f) => !/lucky draw/i.test(f)).map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                  <Check size={18} className="text-amber-300 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {plan && <LuckyDrawBadge double={plan.validity_months >= 3} testId="subscribe-luckydraw" />}

            {/* GST breakdown */}
            <div className="mt-6 rounded-xl bg-amber-500/5 border border-amber-500/15 px-5 py-4 text-sm text-slate-300 space-y-1.5" data-testid="subscribe-breakdown">
              <div className="flex justify-between"><span>Plan price</span><span>{money(base)}</span></div>
              <div className="flex justify-between"><span>GST ({gstPct}%)</span><span>{money(gst)}</span></div>
              <div className="flex justify-between text-amber-100 font-semibold border-t border-amber-500/15 pt-1.5 mt-1"><span>Total payable</span><span data-testid="subscribe-total">{money(total)}</span></div>
            </div>

            {active ? (
              <button data-testid="subscribe-goto-dashboard" onClick={() => nav("/dashboard")}
                className="btn-ghost-gold rounded-full w-full mt-6 py-3.5 flex items-center justify-center gap-2">
                <ShieldCheck size={18} /> You're subscribed — Go to Dashboard
              </button>
            ) : (
              <button data-testid="subscribe-pay-button" onClick={pay} disabled={processing}
                className="btn-gold rounded-full w-full mt-6 py-3.5 flex items-center justify-center gap-2 disabled:opacity-70">
                {processing ? <><Loader2 size={18} className="animate-spin" /> Processing payment…</> : <><Sparkles size={18} /> Pay {money(total)} & Activate</>}
              </button>
            )}
            <p className="mt-3 text-center text-[11px] text-slate-500">Secure payment via Razorpay (demo mode). No card details are stored.</p>
            <div className="mt-5"><Disclaimer compact /></div>
          </div>
        </div>
        <button onClick={() => nav("/dashboard")} className="mt-6 w-full text-center text-sm text-slate-500 hover:text-amber-200">Skip for now</button>
      </div>
    </div>
  );
}
